// Tab 6: Bundle-uri — virtual products that consume component inventory.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Loader2, Trash2, Pencil, Layers, X, Package as PackageIcon,
  AlertOctagon, CheckCircle2, ImageOff, Search, Boxes,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

import {
  listBundles, createBundle, updateBundle, deleteBundle, listItems,
} from "@/lib/inventory";


export default function BundlesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try { setItems(await listBundles()); }
    catch { toast.error("Eroare"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const remove = async (id) => {
    if (!window.confirm("Ștergi acest bundle?")) return;
    try { await deleteBundle(id); await reload(); toast.success("Bundle șters"); }
    catch { toast.error("Eroare"); }
  };

  return (
    <div className="space-y-4" data-testid="bundles-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Boxes className="h-5 w-5 text-primary" />
            Bundle-uri
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Produse virtuale care consumă stocul componentelor la vânzare. Disponibilitatea = min(qty / per_bundle) pe toate componentele.
          </p>
        </div>
        <Button size="sm" onClick={() => setEditing({})} data-testid="bundle-new-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Bundle nou
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">
          <Layers className="h-8 w-8 mx-auto mb-3 opacity-50" />
          <div className="text-sm">Niciun bundle încă.</div>
          <div className="text-xs mt-1">Când un produs Shopify este vândut ca un pachet, mapează-l aici la componentele lui interne.</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((b) => (
            <BundleCard key={b.id} bundle={b} onEdit={() => setEditing(b)} onDelete={() => remove(b.id)} />
          ))}
        </div>
      )}

      <BundleDialog
        editing={editing}
        onClose={() => setEditing(null)}
        onDone={async () => { setEditing(null); await reload(); }}
      />
    </div>
  );
}


function BundleCard({ bundle, onEdit, onDelete }) {
  const rawAvail = bundle.available || 0;
  const avail = Math.max(0, rawAvail);
  const deficit = rawAvail < 0 ? Math.abs(rawAvail) : 0;
  const statusClass = avail > 5 ? "text-emerald-500" : avail > 0 ? "text-amber-500" : "text-red-500";

  return (
    <Card className="p-4 group hover:shadow-md transition" data-testid={`bundle-${bundle.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-display font-semibold text-base truncate">{bundle.name}</div>
          {bundle.match_sku && (
            <div className="text-[11px] font-mono text-muted-foreground mt-0.5">SKU: {bundle.match_sku}</div>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition">
          <button onClick={onEdit} className="text-muted-foreground hover:text-foreground" data-testid={`bundle-edit-${bundle.id}`}>
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button onClick={onDelete} className="text-muted-foreground hover:text-red-500" data-testid={`bundle-del-${bundle.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className={`mt-3 text-3xl font-display font-bold tabular-nums ${statusClass}`}>
        {avail}
      </div>
      <div className="text-[11px] text-muted-foreground -mt-0.5">bundle-uri disponibile</div>
      {deficit > 0 && (
        <div className="mt-1 text-[11px] text-red-500 flex items-center gap-1 font-medium">
          <AlertOctagon className="h-3 w-3" />
          Deficit: {deficit} buc{bundle.bottleneck_sku ? ` · ${bundle.bottleneck_sku}` : ""}
        </div>
      )}
      {deficit === 0 && bundle.bottleneck_sku && avail <= 5 && (
        <div className="mt-1 text-[11px] text-muted-foreground flex items-center gap-1">
          <AlertOctagon className="h-3 w-3 text-amber-500" />
          Bottleneck: <span className="font-mono">{bundle.bottleneck_sku}</span>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-border/60 space-y-1.5">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Componente</div>
        {(bundle.components || []).map((c, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className="h-6 w-6 rounded bg-secondary overflow-hidden shrink-0">
              {c.image ? <img src={c.image} alt="" className="h-full w-full object-cover" /> : <ImageOff className="h-3 w-3 m-1.5 text-muted-foreground" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="truncate">{c.title || c.sku || "—"}</div>
            </div>
            <div className="text-muted-foreground tabular-nums">×{c.qty_per_bundle}</div>
            <div className={`font-mono tabular-nums ${c.warehouse_qty <= 0 ? "text-red-500" : "text-muted-foreground"}`}>
              ({c.warehouse_qty || 0} disp)
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}


function BundleDialog({ editing, onClose, onDone }) {
  const open = !!editing;
  const [form, setForm] = useState({});
  const [components, setComponents] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || "",
        description: editing.description || "",
        match_sku: editing.match_sku || "",
        match_variant_id: editing.match_variant_id || "",
      });
      setComponents(editing.components || []);
      setSearch("");
      setResults([]);
    }
  }, [editing]);

  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await listItems({ search, limit: 8 });
        setResults(r.items || []);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addComponent = (item) => {
    if (components.find((c) => c.inventory_item_id === item.id)) return;
    setComponents((prev) => [...prev, {
      inventory_item_id: item.id,
      variant_id: item.variant_id,
      sku: item.sku,
      title: item.title,
      image: item.image,
      qty_per_bundle: 1,
    }]);
    setSearch("");
    setResults([]);
  };
  const setCompQty = (id, qty) => {
    setComponents((prev) => prev.map((c) => c.inventory_item_id === id ? { ...c, qty_per_bundle: parseInt(qty, 10) || 1 } : c));
  };
  const removeComp = (id) => setComponents((prev) => prev.filter((c) => c.inventory_item_id !== id));

  const submit = async () => {
    if (!form.name?.trim()) return toast.error("Numele este obligatoriu");
    if (!components.length) return toast.error("Adaugă cel puțin o componentă");
    setSaving(true);
    try {
      const body = {
        ...form,
        components: components.map((c) => ({
          inventory_item_id: c.inventory_item_id,
          variant_id: c.variant_id,
          sku: c.sku,
          title: c.title,
          image: c.image,
          qty_per_bundle: c.qty_per_bundle,
        })),
      };
      if (editing.id) await updateBundle(editing.id, body);
      else await createBundle(body);
      toast.success("Bundle salvat");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bundle-dialog">
        <DialogHeader>
          <DialogTitle>{editing?.id ? "Editează bundle" : "Bundle nou"}</DialogTitle>
          <DialogDescription>
            Mapează SKU-ul Shopify la produse interne. Când comanda se confirmă, componentele scad.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nume bundle *">
              <Input value={form.name || ""} onChange={(e) => setF("name", e.target.value)} placeholder="ex: Pachet Drujbă + Sudură" data-testid="bundle-name" />
            </Field>
            <Field label="SKU bundle (cum apare în comanda Shopify)">
              <Input value={form.match_sku || ""} onChange={(e) => setF("match_sku", e.target.value)} placeholder="ex: PACHET_DRUJBA_SUDURA" data-testid="bundle-sku" />
            </Field>
          </div>
          <Field label="Descriere">
            <Textarea value={form.description || ""} onChange={(e) => setF("description", e.target.value)} rows={2} />
          </Field>

          {/* Components */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Componente *</div>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Caută produs (SKU sau titlu)..."
                className="pl-9"
                data-testid="bundle-search-component"
              />
              {results.length > 0 && (
                <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto">
                  {results.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => addComponent(r)}
                      className="w-full text-left px-3 py-2 hover:bg-secondary text-sm flex items-center gap-2"
                      data-testid={`bundle-add-component-${r.id}`}
                    >
                      <div className="h-6 w-6 rounded bg-secondary overflow-hidden shrink-0">
                        {r.image && <img src={r.image} alt="" className="h-full w-full object-cover" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate">{r.title}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">{r.sku || "—"} · stoc: {r.warehouse_qty}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {components.length > 0 ? (
              <div className="space-y-2 max-h-64 overflow-auto" data-testid="bundle-components-list">
                {components.map((c) => (
                  <div key={c.inventory_item_id} className="flex items-center gap-2 bg-secondary/30 rounded p-2">
                    <div className="h-7 w-7 rounded bg-secondary overflow-hidden shrink-0">
                      {c.image && <img src={c.image} alt="" className="h-full w-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{c.title || "—"}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">{c.sku || "—"}</div>
                    </div>
                    <span className="text-xs text-muted-foreground">×</span>
                    <Input
                      type="number"
                      value={c.qty_per_bundle}
                      onChange={(e) => setCompQty(c.inventory_item_id, e.target.value)}
                      className="w-20 h-8 text-center tabular-nums"
                      min="1"
                      data-testid={`bundle-qty-${c.inventory_item_id}`}
                    />
                    <button onClick={() => removeComp(c.inventory_item_id)} className="text-muted-foreground hover:text-red-500">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground p-3 bg-secondary/30 rounded">
                Adaugă cel puțin o componentă din lista de produse de mai sus.
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={submit} disabled={saving} data-testid="bundle-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Salvează
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">{label}</div>
      {children}
    </div>
  );
}
